import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { BillingApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

const BillingResult = ({ cancelled = false }) => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh, user } = useAuth();
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");
  const [attempts, setAttempts] = useState(0);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (cancelled || !sessionId) {
      if (!cancelled && !sessionId) setState("error");
      return undefined;
    }
    let timer;
    const poll = async () => {
      try {
        const data = await BillingApi.status(sessionId);
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("paid");
          refresh();
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("failed");
          return;
        }
      } catch (e) {
        // keep polling; Stripe may still be writing the session
      }
      setAttempts((a) => a + 1);
      if (attempts < 20) timer = setTimeout(poll, 2500);
      else setState("timeout");
    };
    poll();
    return () => timer && clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, cancelled, attempts === 0]);

  const home = user?.role === "admin" ? "/app/billing" : "/me";

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="ll-card w-full max-w-md p-6 text-center" data-testid="billing-result-card">
        {state === "checking" ? (
          <>
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
            <h1 className="mt-4 font-display text-xl font-semibold">Confirming your subscription…</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              This usually takes a couple of seconds.
            </p>
          </>
        ) : null}

        {state === "paid" ? (
          <>
            <CheckCircle2 className="mx-auto h-9 w-9 text-emerald-600" />
            <h1 className="mt-4 font-display text-xl font-semibold">Subscription active</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Your {info?.plan ? `${info.plan} plan` : "plan"} is live
              {info?.seats ? ` for ${info.seats} seats` : ""}. Scan limits are lifted immediately.
            </p>
          </>
        ) : null}

        {["cancelled", "failed", "timeout", "error"].includes(state) ? (
          <>
            <XCircle className="mx-auto h-9 w-9 text-muted-foreground" />
            <h1 className="mt-4 font-display text-xl font-semibold">
              {state === "cancelled" ? "Checkout cancelled" : "Payment not completed"}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {state === "cancelled"
                ? "No charge was made. You can pick a plan whenever you are ready."
                : "We could not confirm the payment. Nothing was activated — please try again."}
            </p>
          </>
        ) : null}

        <div className="mt-6 flex justify-center gap-2">
          <Link to={home}>
            <Button data-testid="billing-result-continue">Back to billing</Button>
          </Link>
          <Link to="/app/dashboard">
            <Button variant="outline">Dashboard</Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BillingResult;
